import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { MessageCircle, ExternalLink, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

/**
 * One-click share button. Opens a popover listing every enabled WhatsApp
 * Community. Clicking one copies the prefilled message to clipboard and
 * opens the community invite link (or wa.me share if no link).
 *
 * Props:
 *   job: { id, job_title, company_name, ... }
 *   variant, size: passed to the Button trigger
 */
const ShareToWhatsAppButton = ({ job, variant = 'outline', size = 'sm' }) => {
  const [communities, setCommunities] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const loadCommunities = async () => {
    if (loaded) return;
    try {
      const r = await api.get('/cms/whatsapp-communities');
      const items = (r.data?.data || []).filter(c => c.enabled);
      setCommunities(items);
    } catch (e) {
      // silent
    } finally {
      setLoaded(true);
    }
  };

  const buildShareText = () => {
    const url = `${window.location.origin}/api/share/job/${job.id}`;
    const title = job.job_title || 'New Job Opening';
    const company = job.company_name || '';
    const location = (job.locations && job.locations[0])
      ? [job.locations[0].city, job.locations[0].district].filter(Boolean).join(', ')
      : '';
    const lines = [
      '📢 *New Job Opening*',
      '',
      `📌 *${title}*`,
      company ? `🏢 ${company}` : '',
      location ? `📍 ${location}` : '',
      '',
      `➡️ Apply: ${url}`,
    ].filter(Boolean);
    return lines.join('\n');
  };

  const shareToCommunity = async (community) => {
    const text = buildShareText();
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(community.id);
      toast.success(`Job message copied! Opening ${community.label}...`);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.warning('Could not copy to clipboard - please paste manually');
    }
    if (community.invite_link) {
      window.open(community.invite_link, '_blank');
    } else {
      // Fall back to wa.me with prefilled text (user picks chat)
      const wa = `https://wa.me/?text=${encodeURIComponent(text)}`;
      window.open(wa, '_blank');
    }
  };

  const shareGeneric = async () => {
    const text = buildShareText();
    try { await navigator.clipboard.writeText(text); } catch {}
    const wa = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(wa, '_blank');
    toast.success('WhatsApp share opened - pick a chat & paste');
  };

  return (
    <Popover onOpenChange={(open) => { if (open) loadCommunities(); }}>
      <PopoverTrigger asChild>
        <Button
          data-testid={`share-whatsapp-btn-${job.id}`}
          variant={variant}
          size={size}
          className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
        >
          <MessageCircle className="w-4 h-4 mr-1" />
          Share
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" align="end">
        <div className="text-xs font-medium text-slate-700 mb-2">Share to WhatsApp Community</div>
        {!loaded ? (
          <div className="text-xs text-slate-400 py-2">Loading…</div>
        ) : communities.length === 0 ? (
          <div className="space-y-2">
            <p className="text-xs text-slate-500">No communities configured. Configure them in <span className="font-medium">Broadcast Settings</span>.</p>
            <Button size="sm" className="w-full" onClick={shareGeneric}>
              <MessageCircle className="w-3.5 h-3.5 mr-1" /> Open WhatsApp Anyway
            </Button>
          </div>
        ) : (
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {communities.map((c) => (
              <button
                key={c.id}
                data-testid={`share-wa-target-${c.id}`}
                onClick={() => shareToCommunity(c)}
                className="w-full text-left px-3 py-2 rounded-md hover:bg-emerald-50 border border-slate-100 flex items-center justify-between gap-2"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{c.label}</div>
                  {c.invite_link && (
                    <div className="text-[11px] text-slate-400 truncate">{c.invite_link}</div>
                  )}
                </div>
                {copiedId === c.id ? (
                  <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                ) : (
                  <ExternalLink className="w-4 h-4 text-slate-400 shrink-0" />
                )}
              </button>
            ))}
            <div className="pt-2 border-t mt-2">
              <button
                onClick={async () => {
                  const text = buildShareText();
                  try { await navigator.clipboard.writeText(text); toast.success('Message copied to clipboard'); } catch { toast.error('Copy failed'); }
                }}
                className="w-full text-xs text-slate-500 hover:text-slate-800 flex items-center justify-center gap-1 py-1.5"
              >
                <Copy className="w-3 h-3" /> Copy message only
              </button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default ShareToWhatsAppButton;
